import { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import API, { getStoredAuthToken, setApiAuthToken } from '../lib/api';

// Safe default prevents destructuring crash when used outside AuthProvider.
const AUTH_CONTEXT_DEFAULT = {
  user: null,
  token: null,
  loading: true,
  permissions: [],
  isAuthenticated: false,
  isSuperAdmin: false,
  isAdmin: false,
  isAuditor: false,
  login: async () => {},
  logout: () => {},
  refreshUser: async () => {},
  hasRole: () => false,
  hasPermission: () => false,
  hasAnyPermission: () => false,
};

const AuthContext = createContext(AUTH_CONTEXT_DEFAULT);
const normalizeRole = (role) => role === "admin" ? "audit_manager" : role;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => getStoredAuthToken());
  const [loading, setLoading] = useState(true);

  // Set axios default header when token changes
  useEffect(() => {
    setApiAuthToken(token);
  }, [token]);

  // Validate token on mount
  useEffect(() => {
    const validate = async () => {
      if (!token) { setLoading(false); return; }
      try {
        setApiAuthToken(token);
        const { data } = await API.get('/auth/me');
        setUser(data);
      } catch (e) {
        setToken(null);
        setUser(null);
      }
      setLoading(false);
    };
    validate();
  }, [token]);

  const login = useCallback(async (email, password) => {
    const { data } = await API.post('/auth/login', { email, password });
    setApiAuthToken(data.token);
    setToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    setApiAuthToken(null);
    setToken(null);
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await API.get('/auth/me');
      setUser(data);
      return data;
    } catch (e) {
      logout();
    }
  }, [logout]);

  const isAuthenticated = !!user && !!token;
  const normalizedRole = normalizeRole(user?.role);
  const permissions = useMemo(() => user?.permissions || user?.perms || [], [user]);
  const isSuperAdmin = normalizedRole === 'super_admin';
  const isAdmin = normalizedRole === 'audit_manager' || isSuperAdmin;
  const isAuditor = normalizedRole === 'auditor' || normalizedRole === 'senior_auditor';

  const hasRole = useCallback((...roles) => {
    return !!user && roles.map(normalizeRole).includes(normalizedRole);
  }, [normalizedRole, user]);

  const hasPermission = useCallback((permission) => {
    return permissions.includes(permission);
  }, [permissions]);

  const hasAnyPermission = useCallback((...requested) => {
    return requested.some((permission) => permissions.includes(permission));
  }, [permissions]);

  return (
    <AuthContext.Provider value={{
      user, token, loading,
      permissions,
      isAuthenticated, isSuperAdmin, isAdmin, isAuditor,
      login, logout, refreshUser, hasRole, hasPermission, hasAnyPermission
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error(
      'useAuth() must be called inside <AuthProvider>. ' +
      'Ensure your component tree is wrapped with <AuthProvider>.'
    );
  }
  return ctx;
};
